import { Stack, Typography } from "@mui/material";
import { Button } from "@/components/elements/Button";
import { AppRoutes } from "@/routes/config";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FC, useState } from "react";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { useDialog } from "@/hooks/useDialog";
import { deleteCompanyApi, setCompanyArchive } from "@/api/services/main/company";
import { toast } from "@/utils/toast";
import { showError } from "@/utils/error";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { getDynamicRoute } from "@/utils/url";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { USER_TYPE } from "@/types/enum";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { queryClient } from "@/constants/queryClient";
import { QUERY_KEYS } from "@/constants/queryKeys";

type TCompanyFooterContainerProps = {
  children?: React.ReactNode;
  isShowSetArchiveButton?: boolean;
  isArchive?: boolean | null | undefined;
  isShowDeleteButton?: boolean;
  hasEndDate?: boolean;
  /** Number of company learners whose accounts are still active (isArchive === false) */
  activeLearnerCount?: number;
  isDetailPage?: boolean;
};

/**
 * Build archive confirmation message per spec:
 */
const buildArchiveConfirmMessage = (hasEndDate: boolean, activeLearnerCount: number): string => {
  const hasActiveLearners = activeLearnerCount > 0;
  if (!hasEndDate) {
    if (hasActiveLearners) {
      return `利用終了日が未入力です。また、外国人${activeLearnerCount}名のアカウントが停止されますが、アーカイブしてもよろしいですか？`;
    }
    return "利用終了日が未入力ですが、このままアーカイブしてもよろしいですか？";
  }
  if (hasEndDate && hasActiveLearners) {
    return `外国人${activeLearnerCount}名のアカウントが停止されますが、アーカイブしてもよろしいですか？`;
  }
  return "所属機関をアーカイブすると新しい学習者を作成できません。このままアーカイブしてもよろしいですか？";
};

export const CompanyFooterContainer: FC<TCompanyFooterContainerProps> = ({ children, isShowSetArchiveButton = false, isArchive, isShowDeleteButton = false, hasEndDate = false, activeLearnerCount = 0, isDetailPage = false }) => {
  const { userType } = useInitSetup();
  const isCompanyUser = userType === USER_TYPE.COMPANY_USER;
  const navigate = useNavigate();
  const archiveConfirmModal = useDialog();
  const deleteCompanyModal = useDialog();
  const { companyId } = useParams();
  const [isSubmit, setIsSubmit] = useState(false);
  const location = useLocation();

  let backRoute = AppRoutes.companyList;

  if (companyId) {
    const { pathname } = location;
    if (pathname.search("edit") > 0 || pathname.search("create") > 0) {
      backRoute = getDynamicRoute(AppRoutes.companyDetail, { companyId });
    }
  }

  const onConfirmSetCompanyArchive = async (archiveStatus: boolean) => {
    if (!companyId) return;
    setIsSubmit(true);
    try {
      if (archiveStatus) {
        const ok = await archiveConfirmModal.show();
        if (!ok) return;
      }

      await setCompanyArchive(Number(companyId), archiveStatus);
      toast.success(API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.COMPANY.FETCH_LIST],
      });
      navigate(AppRoutes.companyList);
    } catch (e) {
      showError(e);
    } finally {
      setIsSubmit(false);
    }
  };
  const onConfirmDeleteCompany = async () => {
    if (!companyId) return;
    setIsSubmit(true);

    try {
      const ok = await deleteCompanyModal.show();
      if (!ok) return;

      await deleteCompanyApi(Number(companyId));
      toast.success(API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.COMPANY.FETCH_LIST],
      });
      navigate(AppRoutes.companyList);
    } catch (e) {
      showError(e);
    } finally {
      setIsSubmit(false);
    }
  };

  const CompanyActionButtons = () => {
    if (isShowDeleteButton) {
      return (
        <Button variant="danger" size="sm" disabled={isSubmit} onClick={onConfirmDeleteCompany}>
          削除
        </Button>
      );
    }
    if (isShowSetArchiveButton) {
      if (isArchive) {
        return (
          <Button variant="ghost" size="sm" disabled={isSubmit} onClick={() => onConfirmSetCompanyArchive(false)}>
            アーカイブから戻す
          </Button>
        );
      }
      return (
        <Button variant="ghost" size="sm" disabled={isSubmit} onClick={() => onConfirmSetCompanyArchive(true)}>
          アーカイブ
        </Button>
      );
    }
    return <></>;
  };

  return (
    <FooterContainer>
      <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" gap={2}>
          <>
            {(!isCompanyUser || !isDetailPage) && (
              <Button size="sm" variant="ghost" onClick={() => navigate(backRoute)}>
                戻る
              </Button>
            )}
            {!isCompanyUser && <CompanyActionButtons />}
          </>
        </Stack>
        {children}
      </Stack>
      <ConfirmSaveModal open={archiveConfirmModal.open} onOk={archiveConfirmModal.confirm} onClose={archiveConfirmModal.cancel}>
        <Stack gap={2}>
          <Typography>{buildArchiveConfirmMessage(hasEndDate, activeLearnerCount)}</Typography>
        </Stack>
      </ConfirmSaveModal>
      <ConfirmDeleteModal
        open={deleteCompanyModal.open}
        message="所属機関を削除してもよろしいですか？"
        onClose={deleteCompanyModal.cancel}
        onOk={deleteCompanyModal.confirm}
      />
    </FooterContainer>
  );
};
